import React from 'react';
import { Card } from './card';

// Alternate approach: Use flag icon SVGs from CDN for better browser compatibility
const FlagIcon = ({ countryCode }) => {
  if (!countryCode || typeof countryCode !== 'string' || !/^[a-zA-Z]{2}$/.test(countryCode)) {
    return <span className="text-xl">🌐</span>;
  }
  const code = countryCode.toLowerCase();
  // Using flagcdn.com CDN for flag icons - works in all browsers
  const flagUrl = `https://flagcdn.com/w80/${code}.png`;
  return (
    <img
      src={flagUrl}
      alt={code}
      title={code.toUpperCase()}
      style={{ width: '32px', height: 'auto', borderRadius: '4px' }}
      loading="lazy"
    />
  );
};

export default function TopCountriesList({ countries, title = "Top Locations", geoFilter = "countries", onGeoFilterChange }) {
  // Debug: Log the received data
  console.log('TopCountriesList received countries:', countries);
  console.log('Title:', title);
  console.log('GeoFilter:', geoFilter);

  // Get the appropriate data based on the filter
  const currentData = countries?.[geoFilter] || [];

  // Get filter display info
  const getFilterInfo = (filter) => {
    switch (filter) {
      case 'countries':
        return { title: 'Top Countries', description: 'Top 10 countries by visitors', icon: '🌍' };
      case 'continents':
        return { title: 'Top Continents', description: 'Top 10 continents by visitors', icon: '🌎' };
      case 'regions':
        return { title: 'Top Regions', description: 'Top 10 regions by visitors', icon: '🏙️' };
      case 'cities':
        return { title: 'Top Cities', description: 'Top 10 cities by visitors', icon: '🏙️' };
      default:
        return { title: 'Top Locations', description: 'Top 10 locations by visitors', icon: '📍' };
    }
  };

  const filterInfo = getFilterInfo(geoFilter);

  // Helper function to resolve item name, subtext and key based on filter
  const getItemDetails = (item, filter) => {
    switch (filter) {
      case 'cities':
        return {
          name: item.city || item.name || 'Unknown City',
          sub: item.country_name || item.country || '',
          key: item.city || item.name || item.country,
          code: item.country
        };
      case 'regions':
        return {
          name: item.region || item.name || 'Unknown Region',
          sub: item.country_name || item.country || '',
          key: item.region || item.name || item.country,
          code: item.country
        };
      case 'continents':
        return {
          name: item.continent || item.name || 'Unknown Continent',
          sub: '',
          key: item.continent || item.name,
          code: ''
        };
      case 'countries':
      default:
        return {
          name: item.country_name || item.country || 'Unknown Country',
          sub: '',
          key: item.country || item.country_name,
          code: item.country
        };
    }
  };

  // Deduplicate and merge items
  const processedItems = (currentData || []).reduce((acc, item) => {
    const details = getItemDetails(item, geoFilter);

    if (!details.name || details.name === 'Unknown' || details.name === 'XX') {
      return acc;
    }

    // For countries, enforce 2-letter ISO code validation for flag icon
    if (geoFilter === 'countries' && (!details.code || typeof details.code !== 'string' || !/^[a-zA-Z]{2}$/.test(details.code))) {
      return acc;
    }

    const existingIndex = acc.findIndex(x => getItemDetails(x, geoFilter).key === details.key);

    if (existingIndex >= 0) {
      acc[existingIndex] = {
        ...acc[existingIndex],
        visitors: (acc[existingIndex].visitors || 0) + (item.visitors || 0)
      };
    } else {
      acc.push(item);
    }

    return acc;
  }, []);

  // Sort by visitors descending and limit to top 10
  const topItems = processedItems
    .sort((a, b) => (b.visitors || 0) - (a.visitors || 0))
    .slice(0, 10);

  if (!topItems || topItems.length === 0) {
    return (
      <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 sm:p-6 border border-slate-100 dark:border-border h-full min-h-[280px] sm:min-h-[450px] flex flex-col transition-colors">
        <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">{filterInfo.title}</h3>
          <select
            value={geoFilter}
            onChange={(e) => onGeoFilterChange && onGeoFilterChange(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-[#021426]"
          >
            <option value="countries">Countries</option>
            <option value="continents">Continents</option>
            <option value="regions">Regions</option>
            <option value="cities">Cities</option>
          </select>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-500 dark:text-slate-400 text-sm">No visitor geographic data available yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 sm:p-6 border border-slate-100 dark:border-border h-full min-h-[280px] sm:min-h-[450px] flex flex-col transition-colors">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">{filterInfo.title}</h3>
        <select
          value={geoFilter}
          onChange={(e) => onGeoFilterChange && onGeoFilterChange(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-[#021426]"
        >
          <option value="countries">Countries</option>
          <option value="continents">Continents</option>
          <option value="regions">Regions</option>
          <option value="cities">Cities</option>
        </select>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 pb-3 border-b border-slate-100 dark:border-slate-800">{filterInfo.description}</p>

      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        <div className="space-y-1">
          {topItems.map((item, index) => {
            const details = getItemDetails(item, geoFilter);
            const visitors = item.visitors || 0;

            return (
              <div key={`${details.key}-${index}`} className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors border-b border-slate-50 dark:border-slate-800/50 last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex-shrink-0 leading-none" title={details.code}>
                    {geoFilter === 'countries' ? (
                      <FlagIcon countryCode={details.code} />
                    ) : (
                      <span className="text-xl">{filterInfo.icon}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex flex-col justify-center">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate block">{details.name}</span>
                    {details.sub && (
                      <span className="text-xs text-slate-400 dark:text-slate-500 truncate block">{details.sub}</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex items-center gap-1.5 flex-shrink-0 ml-3">
                  <span className="font-bold text-slate-900 dark:text-white">{visitors.toLocaleString()}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">visitors</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
